import { CommandItem } from "../../context/command-bar/CommandBarContext";
import { getFavoriteNavItems, getVisibleNavItems } from "../navigation/nav-items";

type GlobalCommandArgs = {
  onLogout: () => void;
  onOpenRpcViewer: () => Promise<void>;
};

/**
 * Builds the list of global commands that are always available in the command bar, regardless of the current page. This includes
 * navigation commands for all visible navigation items, as well as global action commands like opening the RPC viewer and logging out.
 */
export function buildGlobalCommands({
  onLogout,
  onOpenRpcViewer,
}: GlobalCommandArgs): CommandItem[] {
  /**
   * Builds the list of navigation commands for the command bar based on the visible navigation
   * items. This is used both in the main HeaderCommandBar and also in the global command
   * registry to ensure that these commands are always available.
   */
  const navCommands: CommandItem[] = getVisibleNavItems().map((item) => ({
    id: `nav:${item.path}`,
    label: item.label,
    category: item.category,
    keywords: [item.path],
    path: item.path,
  }));

  // The same pages again under Favorites (the palette sorts that category first).
  const favoriteCommands: CommandItem[] = getFavoriteNavItems().map((item) => ({
    id: `fav:${item.path}`,
    label: item.label,
    category: item.category,
    keywords: [item.path],
    path: item.path,
  }));

  /**
   * Defines global action commands that are always available in the command bar, regardless of the current page. These include
   * actions like opening the RPC viewer and logging out. These commands have explicit action functions defined, rather than just
   * navigation paths. They are included in the global command registry so that they can be accessed from anywhere in the app.
   */
  const actionCommands: CommandItem[] = [
    {
      id: "action:rpc-viewer",
      label: "Traffic",
      category: "Mesh",
      keywords: ["rpc", "viewer", "mailbox", "traffic", "mesh"],
      action: onOpenRpcViewer,
    },
    {
      id: "action:logout",
      label: "Logout",
      category: "Actions",
      keywords: ["sign out", "session"],
      action: onLogout,
    },
  ];

  return [...favoriteCommands, ...navCommands, ...actionCommands];
}
